/**
 * Formatting for an operator, not for a customer.
 *
 * The rule everything here follows: never round away a number somebody
 * might be about to act on. `1.2k` is fine on a chart axis and wrong in a
 * queue count, so the compact form is opt-in rather than the default.
 */

const NUMBER = new Intl.NumberFormat("en-GB");
const COMPACT = new Intl.NumberFormat("en-GB", {
  notation: "compact",
  maximumFractionDigits: 1,
});

export function count(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return NUMBER.format(value);
}

/** For axis labels and sparkline peaks, where the exact figure is one
 *  hover away and the space is four characters wide. */
export function compact(value: number): string {
  return value < 1000 ? NUMBER.format(value) : COMPACT.format(value);
}

export function decimal(value: number | null | undefined, places = 1): string {
  if (value === null || value === undefined) return "—";
  return value.toFixed(places);
}

export function percent(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return `${Math.round(value)}%`;
}

const DATE = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
});
const DATETIME = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
});

export function date(iso: string | null | undefined): string {
  if (!iso) return "—";
  return DATE.format(new Date(iso));
}

export function dateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  return DATETIME.format(new Date(iso));
}

/**
 * "3 min ago". Stops at "on 12 Mar 2026" past a week, because beyond that
 * "47 days ago" is a number the reader has to convert back into a date
 * before it means anything.
 */
export function ago(iso: string | null | undefined): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 0) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days <= 7) return `${days}d ago`;
  return `on ${date(iso)}`;
}

/** A duration in seconds, for job runs. */
export function duration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return "—";
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

export function bytes(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(0)} kB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

/** `@handle`, or the display name, or the id's head — in that order. Used
 *  everywhere a person is named, so one person reads the same on every
 *  screen. */
export function personLabel(
  person:
    | { handle: string | null; displayName: string | null; id: string }
    | null
    | undefined,
): string {
  if (!person) return "deleted account";
  if (person.handle) return `@${person.handle}`;
  if (person.displayName) return person.displayName;
  return person.id.slice(0, 8);
}

/** A uuid, shortened to the eight characters an operator actually copies. */
export function shortId(id: string | null | undefined): string {
  return id ? id.slice(0, 8) : "—";
}
